import { PostIRLDocuments, startUploadBatch, updateUploadTrayItem } from '../redux/documentsSlice';
import { isRagIndexableFileName, makeUploadClientId } from './vdrUploadUtils';

const GENERIC_UPLOAD_ERROR = 'Upload failed. Please try again.';

const looksLikeHtml = value => {
    if (typeof value !== 'string') return false;
    const text = value.trim();
    return (
        /<!DOCTYPE\s+html/i.test(text) ||
        /<html[\s>]/i.test(text) ||
        /<\/?[a-z][\s\S]*>/i.test(text.slice(0, 200))
    );
};

const isUsableApiMessage = value => {
    if (typeof value !== 'string') return false;
    const text = value.replace(/\s+/g, ' ').trim();
    if (!text || text.length > 180) return false;
    if (looksLikeHtml(text)) return false;
    return true;
};

/** Prefer short API messages; never surface HTML or stack dumps. */
const extractErrorMessage = payload => {
    if (!payload) return GENERIC_UPLOAD_ERROR;

    if (typeof payload === 'string') {
        return isUsableApiMessage(payload) ? payload.trim() : GENERIC_UPLOAD_ERROR;
    }

    if (typeof payload !== 'object') return GENERIC_UPLOAD_ERROR;

    const candidates = [payload.detail, payload.message, payload.error];
    for (const candidate of candidates) {
        if (isUsableApiMessage(candidate)) return String(candidate).trim();
        if (Array.isArray(candidate) && isUsableApiMessage(candidate[0])) {
            return String(candidate[0]).trim();
        }
    }

    const firstKey = Object.keys(payload)[0];
    const firstVal = firstKey ? payload[firstKey] : null;
    if (Array.isArray(firstVal) && isUsableApiMessage(firstVal[0])) {
        return String(firstVal[0]).trim();
    }
    if (isUsableApiMessage(firstVal)) return String(firstVal).trim();

    return GENERIC_UPLOAD_ERROR;
};

const normalizeUploadResponse = payload => {
    const body = payload?.data?.document_link ? [payload.data] : payload?.data;
    if (Array.isArray(body)) return body[0];
    return body;
};

/**
 * Upload files one-by-one so the Option B tray can show per-file status.
 */
export async function uploadFilesWithProgressTray({
    dispatch,
    files,
    folder,
    workstreamId,
    projectId,
    vdrId,
    uploadBy,
}) {
    const fileList = Array.from(files || []).filter(Boolean);
    if (!fileList.length) return { successCount: 0, failCount: 0 };

    const items = fileList.map(file => ({
        clientId: makeUploadClientId(),
        fileName: file.name,
        folderName: folder?.name || '',
        status: 'queued',
    }));

    dispatch(
        startUploadBatch({
            destinationLabel: folder?.name || '',
            items,
        }),
    );

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const { clientId } = items[i];
        dispatch(updateUploadTrayItem({ clientId, status: 'uploading' }));

        const formData = new FormData();
        formData.append('document', file);
        formData.append('ws_folder', parseInt(folder?.id, 10));
        formData.append('project', projectId);
        formData.append('vdr', vdrId);
        if (uploadBy != null) {
            formData.append('upload_by', uploadBy);
        }

        try {
            const res = await dispatch(PostIRLDocuments({ body: formData, ws_id: workstreamId }));
            if (res?.type === 'POST_IRL_DOC/rejected') {
                failCount += 1;
                dispatch(
                    updateUploadTrayItem({
                        clientId,
                        status: 'failed',
                        error: extractErrorMessage(res?.payload),
                    }),
                );
                continue;
            }

            const doc = normalizeUploadResponse(res?.payload);
            const documentId = doc?.id != null ? Number(doc.id) : undefined;
            successCount += 1;

            if (documentId && isRagIndexableFileName(file.name)) {
                dispatch(
                    updateUploadTrayItem({
                        clientId,
                        status: 'indexing',
                        documentId,
                        wsId: Number(workstreamId),
                        startedAt: Date.now(),
                    }),
                );
            } else {
                dispatch(
                    updateUploadTrayItem({
                        clientId,
                        status: 'ready',
                        documentId,
                        wsId: Number(workstreamId),
                    }),
                );
            }
        } catch (err) {
            failCount += 1;
            dispatch(
                updateUploadTrayItem({
                    clientId,
                    status: 'failed',
                    error: extractErrorMessage(err?.response?.data || err?.message || err),
                }),
            );
        }
    }

    return { successCount, failCount };
}
